/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  applicationUseCases: [],
  applicationLevels: [],
  // Tracks the most recently captured AI response history ID per channel.
  // Channels are kept separate so a text-channel AI suggestion isn't
  // overwritten when the user generates an AI email (and vice versa).
  aiResponseHistoryId: {
    text: null,
    email: null,
  },
  // EAI-1762: the AI-generated draft text itself, exactly as it was written
  // into the composer. Kept so the thumbs can tell an untouched AI draft from
  // one the user has since edited — a rating only describes what Otto wrote.
  aiDraftContent: {
    text: null,
  },
  // EAI-1762: the rating the user gave on the live AI draft. Mirrors what was
  // already posted (the drawer submits immediately, keyed by
  // aiResponseHistoryId) so the thumbs know this draft has been rated and stop
  // offering a second vote. Shape:
  // { aiResponseHistoryId, rating: "GOOD"|"BAD", feedbackOptionIds: [], additionalComments }
  pendingDraftFeedback: {
    text: null,
  },
};

const aiSlice = createSlice({
  name: "aiSlice",
  initialState,
  reducers: {
    setAiApplicationUseCases(state, action) {
      state.applicationUseCases = action.payload;
    },
    setApplicationLevels(state, action) {
      state.applicationLevels = action.payload;
    },
    setTextAiResponseHistoryId(state, action) {
      state.aiResponseHistoryId.text = action.payload ?? null;
      // Clearing the id means there is no AI draft in the composer any more,
      // so the text it was generated from goes with it.
      if (action.payload == null) {
        state.aiDraftContent.text = null;
      }
    },
    setTextAiDraftContent(state, action) {
      state.aiDraftContent.text = action.payload ?? null;
    },
    setEmailAiResponseHistoryId(state, action) {
      state.aiResponseHistoryId.email = action.payload ?? null;
    },
    // Clears both channels in one shot. Useful when context becomes invalid
    // for any stored AI suggestion — e.g. switching to a different lead.
    resetAiResponseHistoryIds(state) {
      state.aiResponseHistoryId.text = null;
      state.aiResponseHistoryId.email = null;
      state.aiDraftContent.text = null;
      state.pendingDraftFeedback.text = null;
    },
    setPendingTextDraftFeedback(state, action) {
      state.pendingDraftFeedback.text = action.payload ?? null;
    },
    clearPendingTextDraftFeedback(state) {
      state.pendingDraftFeedback.text = null;
    },
  },
});

export const {
  setAiApplicationUseCases,
  setApplicationLevels,
  setTextAiResponseHistoryId,
  setTextAiDraftContent,
  setEmailAiResponseHistoryId,
  resetAiResponseHistoryIds,
  setPendingTextDraftFeedback,
  clearPendingTextDraftFeedback,
} = aiSlice.actions;

// Per-channel selectors. Use these instead of reading state.aiData directly
// so the slice's internal shape can change without touching consumers.
export const getTextAiResponseHistoryId = state => state.aiData?.aiResponseHistoryId?.text ?? null;
export const getEmailAiResponseHistoryId = state =>
  state.aiData?.aiResponseHistoryId?.email ?? null;
export const getTextAiDraftContent = state => state.aiData?.aiDraftContent?.text ?? null;

// EAI-1762: pre-send feedback awaiting a message id (text channel).
export const getPendingTextDraftFeedback = state =>
  state.aiData?.pendingDraftFeedback?.text ?? null;

export default aiSlice.reducer;
